import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/navbar';
import createApiInstance from '../utilities/apiAuth';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';

export default function BackupEodList() {
  const navigate = useNavigate();
  const [eods, setEods] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const {user} = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState('newest'); 

  // Function to fetch Backup EOD records
  const fetchEods = useCallback(async () => {
    const api = createApiInstance(navigate);
    try {
      let endpoint = user.role === 'admin' ? '/backup-eods' : `/backup-eods/user/${user.id}`;
      const res = await api.get(endpoint);
  
      const sortedData = res.data.sort((a, b) => 
        sortOrder === 'newest' ? new Date(b.date) - new Date(a.date) : new Date(a.date) - new Date(b.date)
      );
  
      setEods(sortedData);
      console.log('Backup EOD records fetched:', res.data);
    } catch (error) {
      console.error('Error fetching Backup EOD records:', error);
    }
  }, [navigate, user, sortOrder]);
  
  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'));
  };

  useEffect(() => {
    if (user?.role) { // Ensure user.role exists
      fetchEods();
    }
  }, [fetchEods, user?.role]);
  
  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes, seconds] = time.split(':');
    const date = new Date();
    date.setHours(hours, minutes, seconds);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      timeZone: 'UTC',  
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return formattedDate;
  };
  
  // Filtered EOD records based on search term
  const filteredEods = eods.filter((eod) => {
    const formattedDate = formatDate(eod.date); // Convert to MM/DD/YY format
    const emailToSearch = (eod.email || eod.user?.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return (
      emailToSearch.includes(term) ||
      formattedDate.includes(searchTerm) // Compare formatted date
    );
  });  

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEods.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    const totalPages = Math.ceil(filteredEods.length / itemsPerPage) || 1;
    
    // Ensure the currentPage is within bounds
    setCurrentPage((prevPage) => (prevPage > totalPages ? totalPages : prevPage));
  }, [searchTerm, filteredEods.length, itemsPerPage]);

  const handleNextPage = () => {
    if (currentPage < Math.ceil(eods.length / itemsPerPage)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleViewDetails = (eod) => {
    Swal.fire({
      title: 'EOD Details',
      html: `
        <div style="padding: 20px; text-align: left; max-width: 100%;">
          <!-- General Information -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
            <div><strong>SDR:</strong> ${eod.email || eod.user?.email || ''}</div>
            <div><strong>Date:</strong> ${eod.date ? formatDate(eod.date) : ''}</div>
          </div>
    
          <!-- Dialing and Task Details Table -->
          <table style="width: 100%; table-layout: fixed; border-collapse: collapse; margin-bottom: 20px;">
            <colgroup>
              <col style="width: 40%;"> <!-- Label column -->
              <col style="width: 10%;"> <!-- Number column -->
              <col style="width: 40%;"> <!-- Label column -->
              <col style="width: 10%;"> <!-- Number column -->
            </colgroup>
            <thead>
              <tr>
                <th style="border: 1px solid #ccc; padding: 10px; text-align: center; background-color: #f9f9f9;" colspan="2">Call Details</th>
                <th style="border: 1px solid #ccc; padding: 10px; text-align: center; background-color: #f9f9f9;" colspan="2">Task Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Start Time:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.start_time ? formatTime(eod.start_time) : ''}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Overdue Tasks:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.overdue_tasks || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>End Time:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.end_time ? formatTime(eod.end_time) : ''}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Tasks:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_tasks || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Hours Dialed:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_hours_dialed || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Total Tasks:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_total_tasks || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Leads Dialed:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.leads_dialed || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Tasks Completed:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_tasks_completed || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Dials per Hour:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.dials_per_hour || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Tasks Moved to Future:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_tasks_moved_to_future || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Meetings Booked:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_meetings_booked || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Remaining Tasks:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_remaining_tasks || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Emails Created Today:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_emails_created_today || '0'}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Future Tasks Remaining:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.future_tasks_remaining || '0'}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Connects:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.connects || '0'}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Tasks Created:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_tasks_created || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Connection Percentage:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.connection_percentage || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Tasks:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_tasks || 0}</td>
              </tr>
            </tbody>
          </table>
          
          <!-- Reporting Section -->
          <table style="width: 100%; table-layout: fixed; border-collapse: collapse; margin-bottom: 20px;">
            <colgroup>
              <col style="width: 40%;">
              <col style="width: 10%;">
              <col style="width: 40%;">
              <col style="width: 10%;">
            </colgroup>
            <thead>
              <tr>
                <th style="border: 1px solid #ccc; padding: 10px; text-align: center; background-color: #f9f9f9;" colspan="4">Reporting</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>EOD Reported:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.eod_reported ? 'Yes' : 'No'}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>SOD Reported:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.sod_reported ? 'Yes' : 'No'}</td>
              </tr>
            </tbody>
          </table>

          ${eod.sod_tasks ? `
            <div style="font-size: 1.25rem; font-weight: bold; margin-bottom: 10px;">SOD Tasks</div>
            <div style=\"overflow-y: auto; max-height: 200px; padding: 10px; border: 1px solid #ccc; border-radius: 5px; white-space: pre-wrap;\">${eod.sod_tasks}</div>
          ` : ''}
          <table style="width: 100%; table-layout: fixed; border-collapse: collapse; margin-bottom: 20px;">
          <colgroup>
            <col style="width: 40%;"> <!-- Label column -->
            <col style="width: 10%;"> <!-- Number column -->
            <col style="width: 40%;"> <!-- Label column -->
            <col style="width: 10%;"> <!-- Number column -->
          </colgroup>
          <thead>
            <tr>
              <th style="border: 1px solid #ccc; padding: 10px; text-align: center; background-color: #f9f9f9;" colspan="4">Leads</th>
            </tr>
          </thead>
          <tbody>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Leads Left:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_leads_left || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Warm Leads:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_warm_leads || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Leads Contacted:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_leads_contacted || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Hot Leads:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_hot_leads || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Meetings Booked:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_meetings_booked || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Today's Opportunities Fulfilled:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.todays_opportunities_fulfilled || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Warm Leads:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_warm_leads || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Hot Leads:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_hot_leads || 0}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Meetings Booked:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_meetings_booked || 0}</td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;"><strong>Total Opportunities Fulfilled:</strong></td>
                <td style="border: 1px solid #ccc; padding: 10px; word-break: break-word;">${eod.total_opportunities_fulfilled || 0}</td>
              </tr>
          </tbody>
          </table>
          ${eod.notes ? `
            <!-- Notes Section -->
            <div style="font-size: 1.25rem; font-weight: bold; margin-bottom: 10px;">Notes</div>
            <div style="overflow-y: auto; max-height: 200px; padding: 10px; border: 1px solid #ccc; border-radius: 5px;">
              <p style="white-space: pre-wrap; margin-top: 10px;">${eod.notes}</p>
            </div>
          ` : ''}
          
        </div>
      `,
      showConfirmButton: false, // Hide default confirm button
      showCloseButton: true,
      width: '70rem',
      customClass: { popup: 'text-left' },
    });
  };
  
  
  return (
    <div className="w-full flex flex-col min-h-screen bg-gray-900 space-y-8">
      
      <Navbar />
      <div className="mx-auto flex flex-col p-4 justify-center items-center" style={{ width: '110rem' }}>
        <h1 className="mb-5 text-2xl text-hs-background text-center flex-grow">LIST OF EOD RECORDS</h1>
        <div className="flex justify-between gap-3 w-full mb-4">
          <div>
            {user.role === 'admin' ? (
              <input
                type="text"
                placeholder="Search SDR/Date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border rounded p-2 w-[20rem] mr-2"
              />
            ):(
              <input
              type="text"
              placeholder="Search Date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded p-2 w-[20rem] mr-2"
            />
            )}
              <button onClick={toggleSortOrder} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-800">
                {sortOrder === 'newest' ? 'Oldest' : 'Newest'}
              </button>
            </div>
            <div>
            {user.role !== 'admin' && eods.length > 0 && eods[0].spreadSheetID && (
          <a
            href={`https://docs.google.com/spreadsheets/d/${eods[0].spreadSheetID}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 mr-2">
              Open Sheets
            </button>
          </a>
          )}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to page 1 when items per page changes
            }}
            className="border rounded p-2 w-[9.5rem]"
          >
            <option value={10}>10 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        </div>

        <div className="relative overflow-x-auto shadow-lg sm:rounded-lg" style={{ width: '108rem' }}>
          <table className="w-full text-sm text-left rtl:text-right text-white cursor-pointer">
            <thead className="text-xs text-hs-background uppercase bg-hs-light-gray">
              <tr>
                {user.role === 'admin' && <th className="px-4 py-3">SDR</th>}
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Start Time</th>
                <th className="px-4 py-3">End Time</th>
                <th className="px-4 py-3">Total Hours Dialed</th>
                <th className="px-4 py-3">Leads Dialed</th>
                <th className="px-4 py-3">Dials per Hour</th>
                <th className="px-4 py-3">Connects</th>
                <th className="px-4 py-3 whitespace-nowrap">Connection %</th>
                <th className="px-4 py-3">EOD</th>
                <th className="px-4 py-3">SOD</th>
                <th className="px-4 py-3"></th>
                {user.role === 'admin' && (<th className="px-4 py-3"></th>)}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((eod, index) => (
                <tr key={index} className="bg-hs-background text-hs-dark-gray hover:bg-hs-gray">
                  {user.role === 'admin' && <td className="px-4 py-4">{eod.user?.email || 'N/A'}</td>}
                  <td className="px-4 py-4">{formatDate(eod.date)}</td>
                  <td className="px-4 py-4">{formatTime(eod.start_time)}</td>
                  <td className="px-4 py-4">{formatTime(eod.end_time)}</td>
                  <td className="px-4 py-4">{eod.total_hours_dialed || 0}</td>
                  <td className="px-4 py-4">{eod.leads_dialed || 0}</td>
                  <td className="px-4 py-4">{eod.dials_per_hour || 0}</td>
                  <td className="px-4 py-4">{eod.connects || 0}</td>
                  <td className="px-4 py-4">{eod.connection_percentage || 0}</td>
                  <td className="px-4 py-4">{eod.eod_reported ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-4">{eod.sod_reported ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleViewDetails(eod)}
                      className="bg-hs-blue text-white px-4 py-2 rounded hover:bg-hs-dark-blue"
                    >
                      View
                    </button>
                  </td>
                  {user.role === 'admin' && (
                    <td className="px-4 py-4">
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${eod.spreadSheetID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <button className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700">
                          Sheets
                        </button>
                      </a>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          {/* Pagination Controls */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-white mx-4">
              Page {currentPage} of {Math.ceil(filteredEods.length / itemsPerPage) || 1}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage >= Math.ceil(filteredEods.length / itemsPerPage)}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
            >
              Next
            </button>

          </div>
        </div>
      </div>
    );
  }